// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 0356bd21-7ce3-4e39-8634-00a7b3b689ef

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Stack, TextField, InputAdornment, Tabs, Tab, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem,
  FormControl, InputLabel, Autocomplete, Alert, Tooltip, CircularProgress,
  Divider, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InventoryIcon from '@mui/icons-material/Inventory2';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import { TypeBadge } from './ui';
import HelpBubble from './HelpBubble';
import { getConfig } from './config';
import { getIdToken } from './auth';

// Optimum-only master catalog: canonical app definitions, cross-client
// adoption and name reconciliation. Backed by /catalog* on the API
// (catalog_app / catalog_question / catalog_alias, all master-only per
// 015_catalog.sql) rather than the browser's seeded localStorage — this
// screen is the one place that data is actually built and edited.
//
// NO server attachment, NO deployment link, NO heat-map anywhere here. A
// catalog entry describes a PRODUCT, reused across engagements; servers,
// deployments and heat-mapping are what a CLIENT's own fact sheet carries,
// never this screen — see backend/sql/015_catalog.sql for why.

const EMPTY_ENTRY = {
  id: null, name: '', vendorId: '', category: '', typicalHosting: '',
  defaultTime: '', default6R: '', description: '',
  lessonsLearned: '', awsGotchas: '', azureGotchas: '', questions: [],
};

export default function MasterCatalog() {
  const [tab, setTab] = useState(0);
  const [entries, setEntries] = useState([]);
  const [adoption, setAdoption] = useState([]);
  const [reference, setReference] = useState({ vendors: [], picklists: {} });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [q, setQ] = useState('');
  const [unmappedOnly, setUnmappedOnly] = useState(true);

  const [editor, setEditor] = useState(null);   // { entry, pendingRawName? }
  const [mapDialog, setMapDialog] = useState(null); // { rawName, aliasId }
  const [confirmDelete, setConfirmDelete] = useState(null); // catalog entry id

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}`, ...(options.headers || {}) },
    });
    const text = await res.text();
    // An error envelope is often an HTML page, not JSON — the CloudFront
    // rewrite-to-200 trap this codebase keeps rediscovering. Parse defensively
    // so a mis-routed request reads as "request failed (404)" rather than an
    // opaque "Unexpected token <".
    let body = {};
    let parseFailed = false;
    try { body = text ? JSON.parse(text) : {}; } catch (e) { parseFailed = true; }
    if (!res.ok) throw new Error(body.error || body.message || `request failed (${res.status})`);
    if (parseFailed) throw new Error(`The API returned ${res.status} with a body that is not JSON.`);
    return body;
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [cat, adopt, ref] = await Promise.all([
        api('/catalog'), api('/catalog/adoption'), api('/reference'),
      ]);
      setEntries(cat);
      setAdoption(adopt);
      setReference({ vendors: ref.vendors || [], picklists: ref.picklists || {} });
    } catch (e) {
      setErr(e.message || 'failed to load the catalog');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const picklistValues = (key) => ((reference.picklists[key] || {}).values || [])
    .filter((v) => v.active !== false).map((v) => v.label || v.value);

  const filteredEntries = entries.filter(
    (r) => r.name.toLowerCase().includes(q.toLowerCase()) || (r.vendor || '').toLowerCase().includes(q.toLowerCase())
  );
  const filteredAdoption = adoption.filter((r) => (!unmappedOnly || !r.mapped)
    && r.rawName.toLowerCase().includes(q.toLowerCase()));

  // ---- Entry editor ---------------------------------------------------------
  const openNewEntry = (prefillName) => setEditor({ entry: { ...EMPTY_ENTRY, name: prefillName || '' }, pendingRawName: prefillName || null });
  const openEditEntry = async (id) => {
    try {
      const full = await api(`/catalog?id=${encodeURIComponent(id)}`);
      setEditor({ entry: full, pendingRawName: null });
    } catch (e) { setErr(e.message); }
  };

  const saveEntry = async () => {
    const { entry, pendingRawName } = editor;
    if (!entry.name || !entry.name.trim()) { setErr('a name is required'); return; }
    try {
      const { id } = await api('/catalog', { method: 'PUT', body: JSON.stringify(entry) });
      // Any questions edited in this session that are not yet persisted (no
      // real uuid id) are saved after the entry exists, since they carry its id.
      for (const question of entry.questions || []) {
        if (question._dirty || !question.id) {
          await api('/catalog/questions', {
            method: 'PUT',
            body: JSON.stringify({ ...question, catalogAppId: id }),
          });
        }
      }
      // "Promote to new entry" from Reconciliation: the entry did not exist
      // until this save, so the alias mapping happens in the same action
      // rather than a second trip the user would have to remember to make.
      if (pendingRawName) {
        await api('/catalog/aliases', { method: 'PUT', body: JSON.stringify({ rawName: pendingRawName, catalogAppId: id }) });
      }
      setMsg(`Saved ${entry.name}.`);
      setEditor(null);
      await load();
    } catch (e) { setErr(e.message); }
  };

  const deleteEntry = async (id) => {
    try {
      await api(`/catalog?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
      setMsg('Catalog entry removed.');
      setConfirmDelete(null);
      await load();
    } catch (e) { setErr(e.message); }
  };

  const deleteQuestionLocally = (idx) => setEditor((cur) => ({
    ...cur, entry: { ...cur.entry, questions: cur.entry.questions.filter((_, i) => i !== idx) },
  }));
  const addQuestion = () => setEditor((cur) => ({
    ...cur, entry: { ...cur.entry, questions: [...(cur.entry.questions || []), { question: '', category: '', sortOrder: (cur.entry.questions || []).length, _dirty: true }] },
  }));
  const editQuestion = (idx, patch) => setEditor((cur) => {
    const questions = cur.entry.questions.slice();
    questions[idx] = { ...questions[idx], ...patch, _dirty: true };
    return { ...cur, entry: { ...cur.entry, questions } };
  });
  const removeQuestion = async (idx) => {
    const question = editor.entry.questions[idx];
    if (question.id) {
      try { await api(`/catalog/questions?id=${encodeURIComponent(question.id)}`, { method: 'DELETE' }); } catch (e) { setErr(e.message); return; }
    }
    deleteQuestionLocally(idx);
  };

  // ---- Reconciliation actions ------------------------------------------------
  const mapToExisting = async (rawName, catalogAppId) => {
    try {
      await api('/catalog/aliases', { method: 'PUT', body: JSON.stringify({ rawName, catalogAppId }) });
      setMsg(`Mapped "${rawName}".`);
      setMapDialog(null);
      await load();
    } catch (e) { setErr(e.message); }
  };
  const unmap = async (aliasId, rawName) => {
    try {
      await api(`/catalog/aliases?id=${encodeURIComponent(aliasId)}`, { method: 'DELETE' });
      setMsg(`"${rawName}" is unmapped again.`);
      await load();
    } catch (e) { setErr(e.message); }
  };

  const vendorOptions = reference.vendors || [];

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>;

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <InventoryIcon sx={{ color: 'sectionHeader.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Master Application Catalog</Typography>
        <HelpBubble topic="masterCatalog" />
        <Chip size="small" label="Optimum-owned · reused across clients" sx={{ bgcolor: '#eef3ff', color: '#0B5FFF' }} />
        <Box sx={{ flex: 1 }} />
        <TextField size="small" placeholder={tab === 0 ? 'Search catalog…' : 'Search raw names…'} value={q} onChange={(e) => setQ(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        {tab === 0 && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openNewEntry()}>New entry</Button>
        )}
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setErr('')}>{err}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setMsg('')}>{msg}</Alert>}

      <Tabs value={tab} onChange={(e, v) => { setTab(v); setQ(''); }} sx={{ mb: 2, borderBottom: '1px solid #E0E6ED' }}>
        <Tab label={`Catalog (${entries.length})`} />
        <Tab label={`Reconciliation (${adoption.filter((r) => !r.mapped).length} unmapped)`} />
      </Tabs>

      {tab === 0 && (
        <>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            Canonical definitions built once. Knowledge and discovery questions here are Optimum-only and never shown
            to a client; a client's own fact sheet stays a separate, tenant-scoped record even when it names the same
            application.
          </Typography>
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
                  <TableCell>Application</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Typical Hosting</TableCell>
                  <TableCell>Default TIME / 6R</TableCell>
                  <TableCell>Aliases</TableCell>
                  <TableCell>Questions</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEntries.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <TypeBadge type="Application" size={18} />
                        <b>{r.name}</b>
                      </Stack>
                      {r.description && <Typography variant="caption" sx={{ color: '#888' }}>{r.description}</Typography>}
                    </TableCell>
                    <TableCell>{r.vendor || <span style={{ color: '#bbb' }}>—</span>}</TableCell>
                    <TableCell>{r.category || <span style={{ color: '#bbb' }}>—</span>}</TableCell>
                    <TableCell>{r.typicalHosting || <span style={{ color: '#bbb' }}>—</span>}</TableCell>
                    <TableCell>
                      {r.defaultTime && <Chip size="small" label={r.defaultTime} sx={{ mr: 0.5 }} />}
                      {r.default6R && <Chip size="small" variant="outlined" label={r.default6R} />}
                      {!r.defaultTime && !r.default6R && <span style={{ color: '#bbb' }}>—</span>}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={r.aliasCount > 0 ? 'primary' : 'default'} label={r.aliasCount} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={r.questionCount} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditEntry(r.id)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => setConfirmDelete(r.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!filteredEntries.length && (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ color: '#999', py: 3 }}>No catalog entries yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {tab === 1 && (
        <>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: '#666', flex: 1 }}>
              Every distinct Application name across every client, aggregated per tenant so no client's adoption count
              ever blends with another's. Map a raw name to an existing canonical entry, or promote it to a new one.
            </Typography>
            <ToggleButtonGroup size="small" exclusive value={unmappedOnly ? 'unmapped' : 'all'}
              onChange={(e, v) => v && setUnmappedOnly(v === 'unmapped')}>
              <ToggleButton value="unmapped">Unmapped only</ToggleButton>
              <ToggleButton value="all">All names</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
                  <TableCell>Raw application name</TableCell>
                  <TableCell>Instances</TableCell>
                  <TableCell>Clients</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAdoption.map((r) => (
                  <TableRow key={r.rawName} hover>
                    <TableCell><b>{r.rawName}</b></TableCell>
                    <TableCell>{r.totalCount}</TableCell>
                    <TableCell>
                      <Chip size="small" color={r.tenantCount > 1 ? 'primary' : 'default'} label={`${r.tenantCount} tenant${r.tenantCount === 1 ? '' : 's'}`} />
                    </TableCell>
                    <TableCell>
                      {r.mapped
                        ? <Chip size="small" sx={{ bgcolor: '#e8f5e9', color: '#1b5e20' }} label={`→ ${r.canonicalName}`} />
                        : <Chip size="small" sx={{ bgcolor: '#fff3e0', color: '#8a5a00' }} label="Unmapped" />}
                    </TableCell>
                    <TableCell align="right">
                      {r.mapped ? (
                        <>
                          <Tooltip title="Change mapping"><IconButton size="small" onClick={() => setMapDialog({ rawName: r.rawName })}><LinkIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Unmap"><IconButton size="small" onClick={() => unmap(r.aliasId, r.rawName)}><LinkOffIcon fontSize="small" /></IconButton></Tooltip>
                        </>
                      ) : (
                        <>
                          <Tooltip title="Map to existing entry"><IconButton size="small" onClick={() => setMapDialog({ rawName: r.rawName })}><LinkIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Promote to new entry"><IconButton size="small" onClick={() => openNewEntry(r.rawName)}><UpgradeIcon fontSize="small" /></IconButton></Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!filteredAdoption.length && (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ color: '#999', py: 3 }}>Nothing to reconcile.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {/* ---- Map-to-existing dialog ---- */}
      {mapDialog && (
        <Dialog open onClose={() => setMapDialog(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Map "{mapDialog.rawName}"</DialogTitle>
          <DialogContent>
            <Autocomplete
              autoFocus
              options={entries}
              getOptionLabel={(o) => o.name || ''}
              onChange={(e, val) => val && mapToExisting(mapDialog.rawName, val.id)}
              renderInput={(params) => <TextField {...params} label="Canonical catalog entry" margin="normal" />}
            />
          </DialogContent>
          <DialogActions><Button onClick={() => setMapDialog(null)}>Cancel</Button></DialogActions>
        </Dialog>
      )}

      {/* ---- Delete confirm ---- */}
      {confirmDelete && (
        <Dialog open onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Remove this catalog entry?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Its discovery questions and raw-name mappings go with it; any client fact sheet referencing it is left
              untouched but loses the link.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button color="error" onClick={() => deleteEntry(confirmDelete)}>Remove</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ---- Entry editor ---- */}
      {editor && (
        <Dialog open onClose={() => setEditor(null)} maxWidth="md" fullWidth>
          <DialogTitle>
            {editor.entry.id ? `Edit ${editor.entry.name}` : 'New catalog entry'}
            {editor.pendingRawName && (
              <Typography variant="caption" display="block" sx={{ color: '#666', fontWeight: 400 }}>
                Will be mapped to raw name "{editor.pendingRawName}" on save.
              </Typography>
            )}
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Details</Typography>
            <Stack spacing={2} sx={{ mb: 3 }}>
              <TextField label="Name" required fullWidth value={editor.entry.name}
                onChange={(e) => setEditor((c) => ({ ...c, entry: { ...c.entry, name: e.target.value } }))} />
              <Stack direction="row" spacing={2}>
                <Autocomplete
                  fullWidth options={vendorOptions} getOptionLabel={(o) => o.name || ''}
                  value={vendorOptions.find((v) => v.id === editor.entry.vendorId) || null}
                  onChange={(e, val) => setEditor((c) => ({ ...c, entry: { ...c.entry, vendorId: val ? val.id : '' } }))}
                  renderInput={(params) => <TextField {...params} label="Vendor" />}
                />
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select label="Category" value={editor.entry.category || ''}
                    onChange={(e) => setEditor((c) => ({ ...c, entry: { ...c.entry, category: e.target.value } }))}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {picklistValues('applicationType').map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction="row" spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Typical Hosting</InputLabel>
                  <Select label="Typical Hosting" value={editor.entry.typicalHosting || ''}
                    onChange={(e) => setEditor((c) => ({ ...c, entry: { ...c.entry, typicalHosting: e.target.value } }))}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {picklistValues('hostingType').map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Default TIME</InputLabel>
                  <Select label="Default TIME" value={editor.entry.defaultTime || ''}
                    onChange={(e) => setEditor((c) => ({ ...c, entry: { ...c.entry, defaultTime: e.target.value } }))}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {picklistValues('time').map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Default 6R</InputLabel>
                  <Select label="Default 6R" value={editor.entry.default6R || ''}
                    onChange={(e) => setEditor((c) => ({ ...c, entry: { ...c.entry, default6R: e.target.value } }))}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {picklistValues('sixR').map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>
              <TextField label="Description" fullWidth multiline minRows={2} value={editor.entry.description || ''}
                onChange={(e) => setEditor((c) => ({ ...c, entry: { ...c.entry, description: e.target.value } }))} />
            </Stack>

            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Knowledge <HelpBubble topic="catalogLessonsLearned" />
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              De-identified only — no client names, hostnames or counts. This text is reused across every client
              engagement.
            </Alert>
            <Stack spacing={2} sx={{ mb: 3 }}>
              <TextField label="Lessons Learned" fullWidth multiline minRows={2} value={editor.entry.lessonsLearned || ''}
                onChange={(e) => setEditor((c) => ({ ...c, entry: { ...c.entry, lessonsLearned: e.target.value } }))} />
              <TextField label="AWS Configuration Gotchas" fullWidth multiline minRows={2} value={editor.entry.awsGotchas || ''}
                onChange={(e) => setEditor((c) => ({ ...c, entry: { ...c.entry, awsGotchas: e.target.value } }))} />
              <TextField label="Azure Configuration Gotchas" fullWidth multiline minRows={2} value={editor.entry.azureGotchas || ''}
                onChange={(e) => setEditor((c) => ({ ...c, entry: { ...c.entry, azureGotchas: e.target.value } }))} />
            </Stack>

            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
                Discovery questions <HelpBubble topic="catalogQuestions" />
              </Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addQuestion}>Add question</Button>
            </Stack>
            <Stack spacing={1}>
              {(editor.entry.questions || []).map((question, idx) => (
                <Stack key={question.id || `new-${idx}`} direction="row" spacing={1} alignItems="center">
                  <TextField size="small" fullWidth placeholder="Question text" value={question.question || ''}
                    onChange={(e) => editQuestion(idx, { question: e.target.value })} />
                  <TextField size="small" sx={{ width: 200 }} placeholder="Category (e.g. hosting, security)"
                    value={question.category || ''} onChange={(e) => editQuestion(idx, { category: e.target.value })} />
                  <IconButton size="small" onClick={() => removeQuestion(idx)}><DeleteIcon fontSize="small" /></IconButton>
                </Stack>
              ))}
              {!(editor.entry.questions || []).length && (
                <Typography variant="caption" sx={{ color: '#999' }}>No discovery questions yet.</Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditor(null)}>Cancel</Button>
            <Button variant="contained" onClick={saveEntry}>Save</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
